import { formattedNumber, getTags, translate } from "@kaltura/mediaspace-shared-utils";
import { SearchAndSelectEntry } from "@kaltura/mediaspace-shared-types";
import { ReactNode } from "react";
import { EntryNameCell, EntryNameCellSkeleton } from "./EntryNameCell";
import { KalturaPlayableEntry } from "kaltura-typescript-client/api/types/KalturaPlayableEntry";
import { DateCell } from "./DateCell";
import { EntryTagsCell } from "./EntryTagsCell";
import { SearchAndSelectColumnId } from "./SearchAndSelectColumnId";

interface SearchAndSelectColumn {
    /** Translated column title (a callback because translate() shouldn't run in the global scope) */
    title: () => string;
    /** The contents of the cell */
    cell: (entry: SearchAndSelectEntry) => ReactNode;
    /** The contents of the cell for the skeleton */
    skeleton?: ReactNode;
    sort?: {
        ascValue: string;
        descValue: string;
        defaultDesc?: boolean;
    };
    /** Column's fixed width, in pixels */
    width?: number;
    /** Column's minimal width, in pixels */
    minWidth?: number;
}

/**
 * Definitions of media table columns, mapped by column ID
 */
export const columnsMap: Record<SearchAndSelectColumnId, SearchAndSelectColumn> = {
    [SearchAndSelectColumnId.checkbox]: {
        title: () => translate("Select"),
        cell: (entry) => <span />,
        width: 16,
    },
    [SearchAndSelectColumnId.name]: {
        title: () => translate("Name"),
        cell: (entry) => <EntryNameCell entry={entry} />,
        skeleton: <EntryNameCellSkeleton />,
        sort: {
            ascValue: "name",
            descValue: "-name",
        },
        minWidth: 300,
    },
    [SearchAndSelectColumnId.id]: {
        title: () => translate("ID"),
        cell: (entry) => entry.entry.id,
        width: 120,
    },
    [SearchAndSelectColumnId.mediaType]: {
        title: () => translate("Type"),
        cell: (entry) => entry.mediaTypeName,
        width: 100,
    },
    [SearchAndSelectColumnId.tags]: {
        title: () => translate("Tags"),
        cell: (entry) => <EntryTagsCell tags={getTags(entry.entry.tags)} />,
        width: 200,
    },
    [SearchAndSelectColumnId.plays]: {
        title: () => translate("Plays"),
        cell: (entry) => formattedNumber((entry.entry as KalturaPlayableEntry).plays),
        width: 70,
    },
    [SearchAndSelectColumnId.views]: {
        title: () => translate("Views"),
        cell: (entry) => formattedNumber((entry.entry as KalturaPlayableEntry).views),
        width: 80,
    },
    [SearchAndSelectColumnId.creationDate]: {
        title: () => translate("Created at"),
        cell: (entry) => <DateCell timestamp={entry.entry.createdAt} />,
        sort: {
            ascValue: "createdAtAsc",
            descValue: "recent",
            defaultDesc: true,
        },
        width: 160,
    },
    [SearchAndSelectColumnId.updateDate]: {
        title: () => translate("Updated at"),
        cell: (entry) => <DateCell timestamp={entry.entry.updatedAt} />,
        sort: {
            ascValue: "updatedAtAsc",
            descValue: "updatedAtDesc",
            defaultDesc: true,
        },
        width: 160,
    },
};
